/* Renderer del diagramma — porting fedele di flusso-urgenze.html
   (draw 1268-1528, drawHead 1186-1219, splitFirst/inv 1530-1538).
   Rispetto all'originale: niente animazione FLIP (solo output statico) e i
   gestori di eventi sono opzionali (iniettati dall'app). Il colore degli attori
   viene da participant.colors[tema]; i colori del testo inline dal COLORMAPS. */
import {
  anyFilter,
  colX,
  computeLayout,
  computeVisible,
  COLORMAPS,
  firstLine,
  F_COND,
  F_NOTE,
  L,
  parseRuns,
  plain,
  splitRunsByLine,
  THEME,
  type DiagramState,
  type FlowStep,
  type LayoutOut,
  type PEvent,
  type PreparedModel,
  type Run,
  type ThemePalette,
} from "@fullsteps/shared";
import { arrowHead, drawIcon, E, measureText, textRuns, wrapInto } from "./svg.js";

type MsgRaw = Extract<FlowStep, { t: "msg" }>;
type NoteRaw = Extract<FlowStep, { t: "note" }>;

export interface RenderHandlers {
  onSelectMsg?: (n: number) => void;
  onTogglePhase?: (id: string) => void;
}
export interface RenderResult {
  lay: LayoutOut;
  visCount: number;
  total: number;
}

function inv(c: string, colorMap: Record<string, string>): string {
  for (const k in colorMap) if (colorMap[k] === c) return k;
  return c;
}
/** Solo la prima riga in linea, re-serializzata col markup di colore. */
function splitFirst(text: string, colorMap: Record<string, string>): string {
  const runs = parseRuns(text, colorMap);
  const out: Run[] = [];
  for (const r of runs) {
    const i = r.text.indexOf("\n");
    if (i === -1) out.push(r);
    else {
      out.push({ text: r.text.slice(0, i), color: r.color });
      break;
    }
  }
  return out
    .map((r) => (r.color ? `<color:${inv(r.color, colorMap)}>` : "") + r.text + (r.color ? "</color>" : ""))
    .join("");
}

function renderHead(
  head: SVGElement,
  model: PreparedModel,
  S: DiagramState,
  TH: ThemePalette,
  W: number,
): void {
  head.textContent = "";
  head.setAttribute("viewBox", `0 0 ${W} ${L.headH}`);
  head.setAttribute("width", String(W * S.zoom));
  head.setAttribute("height", String(L.headH * S.zoom));
  head.appendChild(E("rect", { x: 0, y: 0, width: W, height: L.headH, fill: TH.headBg }));
  head.appendChild(E("line", { x1: 0, y1: L.headH - 0.5, x2: W, y2: L.headH - 0.5, stroke: TH.headLine }));

  model.doc.participants.forEach((p, idx) => {
    const cx = colX(idx);
    const bw = L.colW - 30;
    const x = cx - bw / 2;
    const y = 20;
    const h = 54;
    const on = !S.actors.size || S.actors.has(p.id);
    const color = p.colors[S.theme];
    const g = E("g", { class: on ? "" : "dim" });
    g.appendChild(
      E("rect", {
        x, y, width: bw, height: h, rx: 10,
        fill: TH.cardFill, stroke: on ? TH.cardOn : TH.cardOff, "stroke-width": 1,
        "stroke-dasharray": p.temp ? "6 4" : null,
      }),
    );
    g.appendChild(E("rect", { x: x + 1, y: y + 1, width: 3.5, height: h - 2, rx: 2, fill: color, opacity: 0.9 }));
    g.appendChild(drawIcon(p.icon || (p.kind === "actor" ? "user" : "building-2"), x + 20, y + h / 2, 20, color));
    wrapInto(g, p.name, x + 36, y + 23, bw - 48, "hdname", 2);
    g.appendChild(
      E("text", { x: x + 36, y: y + h - 9, class: "hdkind" },
        (p.kind === "actor" ? "ACTOR" : "PARTICIPANT") + (p.temp ? " · TEMP" : "")),
    );
    g.appendChild(E("title", undefined, p.name + (p.temp ? " — attore temporaneo (create/destroy)" : "")));
    head.appendChild(g);
  });

  const gh = E("g", { id: "gutterhead" });
  gh.appendChild(E("rect", { x: 0, y: 0, width: L.gutterX + 14, height: L.headH - 1, fill: TH.gutterBg }));
  gh.appendChild(E("text", { x: L.gutterX, y: L.headH - 28, class: "numlab", "text-anchor": "end" }, "#"));
  head.appendChild(gh);
}

export function renderDiagram(
  head: SVGElement,
  diag: SVGElement,
  model: PreparedModel,
  S: DiagramState,
  handlers: RenderHandlers = {},
): RenderResult {
  document.documentElement.setAttribute("data-theme", S.theme);
  const TH = THEME[S.theme];
  const colorMap = COLORMAPS[S.theme];
  const W = model.width;
  const px = (id: string): number => colX(model.partIdx.get(id) ?? 0);
  const colorOf = (id: string): string => model.partById.get(id)?.colors[S.theme] ?? "#888";

  const vis = computeVisible(model, S);
  const noFilter = !anyFilter(S);
  const shown = (m: PEvent): boolean => noFilter || vis.has(m.n!);
  const lay = computeLayout(model, S, vis, measureText);
  const H = lay.height;

  renderHead(head, model, S, TH, W);

  diag.textContent = "";
  diag.setAttribute("viewBox", `0 0 ${W} ${H}`);
  diag.setAttribute("width", String(W * S.zoom));
  diag.setAttribute("height", String(H * S.zoom));

  const gBands = E("g");
  const gLife = E("g");
  const gFrames = E("g");
  const gBars = E("g");
  const gSegs = E("g");
  const gNotes = E("g");
  const gMsgs = E("g");
  const gGutter = E("g", { id: "gutter" });
  for (const g of [gBands, gLife, gFrames, gBars, gNotes, gMsgs, gSegs, gGutter]) diag.appendChild(g);
  gGutter.appendChild(E("rect", { x: 0, y: 0, width: L.gutterX + 14, height: H, fill: TH.gutterBg }));
  gGutter.appendChild(E("line", { x1: L.gutterX + 14, y1: 0, x2: L.gutterX + 14, y2: H, stroke: TH.gutterLine }));

  /* bande di fase */
  for (const { p, y } of lay.phases) {
    const bx = L.gutterX + 22;
    const bw = W - bx - 18;
    const nPass = p.msgs.length;
    const g = E("g", {
      class: "band", role: "button", tabindex: "0",
      "aria-expanded": String(!p.closed), "data-phase": p.id,
    });
    g.appendChild(E("rect", { class: "bandbg", x: bx, y: y - 19, width: bw, height: 38, rx: 12, fill: p.closed ? TH.bandClosed : TH.bandOpen }));
    g.appendChild(E("rect", { x: bx, y: y - 19, width: 34, height: 38, rx: 12, fill: TH.badge }));
    g.appendChild(E("rect", { x: bx + 22, y: y - 19, width: 12, height: 38, fill: TH.badge }));
    g.appendChild(E("text", { x: bx + 17, y: y + 4, class: "phasenum", "text-anchor": "middle" }, "F" + p.num));
    const cx0 = bx + 50;
    const cy0 = y - 1;
    g.appendChild(
      E("path", {
        class: "chev", "stroke-linecap": "round", "stroke-linejoin": "round",
        fill: "none", stroke: TH.chev, "stroke-width": 1.7,
        d: p.closed
          ? `M${cx0 - 2},${cy0 - 4.5} L${cx0 + 3},${cy0} L${cx0 - 2},${cy0 + 4.5}`
          : `M${cx0 - 4.5},${cy0 - 2} L${cx0},${cy0 + 3} L${cx0 + 4.5},${cy0 - 2}`,
      }),
    );
    g.appendChild(E("text", { x: bx + 66, y: y + 5, class: "phaselab" }, p.title));
    const wt = measureText(p.title, "16px 'Rozha One', Georgia, serif");
    g.appendChild(
      E("text", { x: bx + 80 + wt, y: y + 5, class: "phasesub" },
        p.closed ? "— chiusa · " + nPass + (nPass === 1 ? " passo" : " passi") : (p.sub ?? "")),
    );
    if (p.closed)
      g.appendChild(E("line", { x1: bx + 4, y1: y + 26, x2: bx + bw - 4, y2: y + 26, stroke: TH.bandDash, "stroke-dasharray": "2 6", "stroke-linecap": "round", "stroke-width": 1.4 }));
    g.appendChild(E("title", undefined, "Fase " + p.num + ": " + p.title + " — " + nPass + " passi. Clic per " + (p.closed ? "aprire" : "chiudere") + " la fase."));
    if (handlers.onTogglePhase) {
      const toggle = () => handlers.onTogglePhase!(p.id);
      g.addEventListener("click", toggle);
      g.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      });
    }
    gBands.appendChild(g);
  }

  /* linee di vita */
  for (const p of model.doc.participants) {
    if (p.temp) continue;
    const on = !S.actors.size || S.actors.has(p.id);
    gLife.appendChild(E("line", { x1: px(p.id), y1: 0, x2: px(p.id), y2: H - 24, stroke: on ? TH.lifeOn : TH.lifeOff, "stroke-width": 1, "stroke-dasharray": "1 6", "stroke-linecap": "round" }));
  }
  for (const s of lay.segs) {
    const color = colorOf(s.a);
    const name = model.partById.get(s.a)?.name ?? s.a;
    const x = px(s.a);
    const g = E("g", { class: s.dim ? "dim" : "" });
    g.appendChild(E("line", { x1: x, y1: s.y0, x2: x, y2: s.y1, stroke: color, "stroke-width": 1, "stroke-dasharray": "1 6", "stroke-linecap": "round", opacity: 0.7 }));
    g.appendChild(E("rect", { x: x - L.boxW / 2, y: s.boxY - L.boxH / 2, width: L.boxW, height: L.boxH, rx: 10, fill: TH.segFill, stroke: color, "stroke-width": 1.1, "stroke-dasharray": "6 4" }));
    g.appendChild(E("text", { x, y: s.boxY + 4, class: "tempname", "text-anchor": "middle", fill: color }, name));
    g.appendChild(E("rect", { x: x - 18, y: s.y1! - 3, width: 36, height: 5, rx: 2.5, fill: color, opacity: 0.85 }));
    g.appendChild(E("title", undefined, name + " — attore temporaneo: create → destroysilent"));
    gSegs.appendChild(g);
  }

  /* riquadri alt / opt / loop */
  for (const f of lay.frames) {
    const b = f.b;
    const y1 = f.y1 || f.y0 + 40;
    const dimF = !noFilter && S.mode === "dim" && !b.branches.some((br) => br.msgs.some((m) => vis.has(m.n!)));
    const g = E("g", { class: dimF ? "dim" : "" });
    g.appendChild(E("rect", { x: f.x0, y: f.y0, width: f.x1! - f.x0!, height: y1 - f.y0, rx: 14, fill: TH.frameFill, "fill-opacity": TH.frameBase + b.depth * TH.frameStep, stroke: TH.frameStroke, "stroke-width": 1 }));
    for (const { br, y, head: isHead } of f.branchY) {
      if (!br.visible) continue;
      let cx = f.x0! + 16;
      const ly = isHead ? f.y0 : y - 15;
      if (isHead) {
        const kw = measureText(b.kind.toUpperCase(), "10px ui-monospace, monospace") + 22;
        g.appendChild(E("rect", { x: f.x0! + 14, y: ly - 11, width: kw, height: 22, rx: 11, fill: TH.pillFill, stroke: TH.pillStroke, "stroke-width": 1 }));
        g.appendChild(E("text", { x: f.x0! + 14 + kw / 2, y: ly + 4, class: "blocklab", "text-anchor": "middle" }, b.kind.toUpperCase()));
        cx = f.x0! + 14 + kw + 9;
      }
      const runs = parseRuns(br.cond, colorMap);
      runs.unshift({ text: "[", color: null });
      runs.push({ text: "]", color: null });
      g.appendChild(textRuns(cx, ly + 4, runs, { cls: "condlab", font: F_COND, max: f.x1! - cx - 18 }));
    }
    for (const dy of f.dividers)
      g.appendChild(E("line", { x1: f.x0! + 1, y1: dy, x2: f.x1! - 1, y2: dy, stroke: TH.divider, "stroke-dasharray": "1 5", "stroke-linecap": "round", "stroke-width": 1.2 }));
    g.appendChild(E("title", undefined, b.kind + " — " + b.branches.map((br) => "[" + plain(br.cond) + "]").join(" / ")));
    gFrames.appendChild(g);
  }

  /* barre di attivazione */
  for (const bar of lay.bars) {
    const color = colorOf(bar.a);
    const x = px(bar.a) - L.actW / 2 + bar.depth * L.actStep;
    const h = Math.max(6, bar.y1 - bar.y0);
    const g = E("g", { class: S.actors.size && !S.actors.has(bar.a) ? "dim" : "" });
    g.appendChild(E("rect", { x, y: bar.y0, width: L.actW, height: h, rx: 5, fill: TH.barBase }));
    g.appendChild(E("rect", { x, y: bar.y0, width: L.actW, height: h, rx: 5, fill: color, "fill-opacity": 0.13, stroke: color, "stroke-opacity": 0.7, "stroke-width": 1 }));
    gBars.appendChild(g);
  }

  /* note */
  for (const n of lay.notes) {
    const raw = n.ev.raw as NoteRaw;
    const g = E("g", { class: n.dim ? "dim" : "" });
    g.appendChild(E("rect", { x: n.x, y: n.y, width: n.w, height: n.h, rx: 10, fill: TH.noteFill, stroke: TH.noteStroke }));
    g.appendChild(E("rect", { x: n.x, y: n.y + 9, width: 3, height: n.h - 18, rx: 2, fill: TH.noteBar }));
    const lines = splitRunsByLine(parseRuns(raw.text, colorMap));
    lines.forEach((runs, i) =>
      g.appendChild(textRuns(n.x + L.notePadX, n.y + L.notePadY + 12 + i * L.noteLine, runs, { cls: "notetext", font: F_NOTE })),
    );
    g.appendChild(E("title", undefined, "Nota su " + (model.partById.get(raw.over)?.name ?? raw.over) + ":\n" + plain(raw.text)));
    gNotes.appendChild(g);
  }

  /* messaggi */
  let visCount = 0;
  const barAt = (id: string, y: number) => {
    let best: (typeof lay.bars)[number] | null = null;
    for (const b of lay.bars)
      if (b.a === id && b.y0 <= y + 1 && b.y1 >= y - 1) if (!best || b.depth > best.depth) best = b;
    return best;
  };
  const sourceX = (m: PEvent): number => {
    const raw = m.raw as MsgRaw;
    const bar = barAt(raw.from, m.y!);
    const dir = px(raw.to) >= px(raw.from) ? 1 : -1;
    return px(raw.from) + dir * (bar ? L.actW / 2 + bar.depth * L.actStep : 1);
  };
  const targetX = (m: PEvent, yc: number): number => {
    const raw = m.raw as MsgRaw;
    const seg = lay.segs.find((s) => s.a === raw.to && Math.abs(s.boxY - yc) < 3);
    const dir = px(raw.to) >= px(raw.from) ? 1 : -1;
    if (seg) return px(raw.to) - dir * L.boxW / 2;
    const bar = barAt(raw.to, yc);
    return px(raw.to) - dir * (bar ? L.actW / 2 + bar.depth * L.actStep : 1);
  };

  for (const r of lay.rows) {
    const first = r.msgs[0]!;
    const firstRaw = first.raw as MsgRaw;
    const selHere = S.sel != null && r.msgs.some((m) => m.n === S.sel);
    const g = E("g", {
      class: "msg" + (r.dim ? " dim" : "") + (selHere ? " sel" : ""),
      tabindex: "0", role: "button", "data-n": firstRaw && first.n,
    });
    const yc = r.y;
    g.appendChild(E("rect", { class: "hit", x: L.gutterX + 16, y: yc - 22, width: W - L.gutterX - 36, height: 44, rx: 10 }));

    const color = colorOf(firstRaw.from);
    const numT = E("text", { x: L.gutterX, y: yc + 4, "text-anchor": "end", class: "numlab" + (r.dim ? " dim" : "") + (selHere ? " selnum" : "") }, r.label);
    gGutter.appendChild(numT);

    for (const m of r.msgs) if (shown(m)) visCount++;

    if (r.sync) {
      const ox = px(firstRaw.from);
      const targets = r.msgs.map((m) => ({ m, x: targetX(m, yc), on: shown(m) }));
      const useT = S.mode === "hide" && !noFilter ? targets.filter((t) => t.on) : targets;
      if (useT.length) {
        const all = useT.map((t) => t.x).concat([ox]);
        const x0 = Math.min(...all);
        const x1 = Math.max(...all);
        g.appendChild(E("line", { x1: x0, y1: yc, x2: x1, y2: yc, stroke: color, "stroke-width": 1.4, "stroke-linecap": "round", opacity: 0.92 }));
        g.appendChild(E("circle", { cx: ox, cy: yc, r: 9, fill: color, opacity: 0.13 }));
        g.appendChild(E("circle", { cx: ox, cy: yc, r: 4, fill: color }));
        for (const t of useT) {
          const dir = t.x >= ox ? 1 : -1;
          const h = arrowHead(t.x, yc, dir, color);
          if (!t.on && !noFilter) h.setAttribute("opacity", "0.22");
          g.appendChild(h);
          if ((t.m.lines?.length ?? 1) > 1)
            g.appendChild(E("text", { x: t.x - dir * 17, y: yc - 8, class: "extra", "text-anchor": "middle" }, "⋯"));
        }
        const mid = (x0 + x1) / 2;
        g.appendChild(textRuns(mid, yc - 13, parseRuns(splitFirst(firstRaw.text, colorMap), colorMap), { cls: "lab", anchor: "middle", max: x1 - x0 - 40 }));
        g.appendChild(E("text", { x: ox, y: yc + 22, class: "syncchip", "text-anchor": "middle" }, "◆ contemporanei · " + r.msgs.length + " destinatari"));
      }
    } else {
      const m = first;
      const raw = firstRaw;
      const x1 = sourceX(m);
      const x2 = targetX(m, yc);
      const dir = x2 >= x1 ? 1 : -1;
      g.appendChild(E("line", { x1, y1: yc, x2: x2 - dir * 8, y2: yc, stroke: color, "stroke-width": 1.4, "stroke-linecap": "round", opacity: 0.92 }));
      g.appendChild(arrowHead(x2, yc, dir, color));
      const mid = (x1 + x2) / 2;
      const avail = Math.max(230, Math.abs(x2 - x1) + 160);
      g.appendChild(textRuns(mid, yc - 10, parseRuns(splitFirst(raw.text, colorMap), colorMap), { cls: "lab", anchor: "middle", max: avail }));
      const nLines = m.lines?.length ?? 1;
      if (nLines > 1) {
        const k = nLines - 1;
        g.appendChild(E("text", { x: mid, y: yc + 16, class: "extra", "text-anchor": "middle" }, k === 1 ? "⋯ altra 1 riga" : "⋯ altre " + k + " righe"));
      }
      if (raw.deadline) {
        const dx = Math.max(x1, x2) + 16;
        g.appendChild(E("text", { x: dx, y: yc + 4, class: "dlchip" }, "⏱ " + raw.deadline.short));
      }
    }
    g.appendChild(
      E("title", undefined,
        r.msgs.map((m) => {
          const mr = m.raw as MsgRaw;
          return "#" + m.n + " " + (model.partById.get(mr.from)?.name ?? mr.from) + " → " + (model.partById.get(mr.to)?.name ?? mr.to) + "\n" + plain(mr.text);
        }).join("\n\n")),
    );
    g.setAttribute(
      "aria-label",
      "Passo " + r.label + ", da " + (model.partById.get(firstRaw.from)?.name ?? firstRaw.from) + " a " +
        r.msgs.map((m) => model.partById.get((m.raw as MsgRaw).to)?.name ?? "").join(", ") + ": " + firstLine(firstRaw.text),
    );
    if (handlers.onSelectMsg) {
      const sel = () => handlers.onSelectMsg!(first.n!);
      g.addEventListener("click", sel);
      g.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          sel();
        }
      });
    }
    gMsgs.appendChild(g);
  }

  return { lay, visCount, total: model.total };
}
